import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import UiButton from 'garaje/components/ui-button';
import type MetricsService from 'garaje/services/metrics';
import type SessionStorageService from 'garaje/services/session-storage';
import type WindowLocationService from 'garaje/services/window-location';
import { isServiceAvailabilityError } from 'garaje/utils/catch-service-error';

interface ServiceUnavailableSignature {
  Element: HTMLDivElement;
  Args: {
    error?: unknown;
    section?: string;
  };
}

export default class ServiceUnavailable extends Component<ServiceUnavailableSignature> {
  @service declare metrics: MetricsService;
  @service declare router: RouterService;
  @service declare sessionStorage: SessionStorageService;
  @service declare windowLocation: WindowLocationService;

  get isAvailabilityError(): boolean {
    // with no error given, the caller is showing a degraded panel for a known
    // backend outage, so use the connectivity copy
    if (this.args.error === undefined) return true;
    return isServiceAvailabilityError(this.args.error);
  }

  @action
  retry(): void {
    this.sessionStorage.setItem('recovery_retry_attempt', 'user');
    let count = parseInt(this.sessionStorage.getItem('error_state_retry_count') || '0', 10);
    count++;
    this.sessionStorage.setItem('error_state_retry_count', `${count}`);
    this.windowLocation.reload();
  }

  @action
  trackStatusClick(): void {
    this.metrics.trackEvent('Status Link Clicked', {
      current_url: this.router.currentURL,
      section: this.args.section,
    });
  }

  <template>
    <div class="flex items-center justify-center h-full py-16" data-test-service-unavailable ...attributes>
      <div
        class="flex-auto min-w-0 min-h-0 flex-col mx-auto shadow bg-white py-16 px-0 animated fadeInUpSmall"
        style="max-width: 760px;"
      >
        <div class="error-disconnected"></div>
        {{#if this.isAvailabilityError}}
          <h1 class="mt-8 text-center" data-test-service-unavailable-title>Envoy is having trouble connecting</h1>
          <p class="my-4 text-center">
            This part of the dashboard could not be loaded. Check Envoy&apos;s
            <a
              href="https://status.envoy.com"
              data-test-envoy-status-link
              rel="noopener"
              target="_blank"
              {{on "click" this.trackStatusClick}}
            >
              status
            </a>
            or try again in a moment.
          </p>
        {{else}}
          <h1 class="mt-8 text-center" data-test-service-unavailable-title>Something went wrong</h1>
          <p class="my-4 text-center">
            Try again or, if the problem persists,
            <a href="https://envoy.help/en/articles/3330118-customer-support" target="_blank" rel="noopener">
              contact support</a>.
          </p>
        {{/if}}
        <p class="mt-8 mb-0 text-center">
          <UiButton data-test-service-unavailable-retry class="btn btn--small btn--brand" {{on "click" this.retry}}>
            Try again
          </UiButton>
        </p>
      </div>
    </div>
  </template>
}
